// Direct imports
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { Observer } from "@babylonjs/core/Misc/observable";
import { Scene } from "@babylonjs/core/scene";
import { TraceBehavior } from "./tracebehavior";
import { RuntimeToggleLatch, type RuntimeToggleable } from "./runtimeToggleable";
import { TetherData } from "../scenedata/behaviors/tetherdata";
import type { SceneObject } from "../core/SceneObject";

// Shared across all Tether instances — safe because observers run synchronously.
const _tmpTargetPos = new Vector3();
const _tmpSelfPos = new Vector3();
const _tmpDelta = new Vector3();
const _tmpDesired = new Vector3();
const _tmpStep = new Vector3();

/** Comfort slack as a fraction of the resting distance: the target leaning
 *  in closer than keepDistance × (1 − REST_BAND) pushes the object back.
 *  Fixed internal constant (not authored in TetherData). */
const REST_BAND = 0.25;

/**
 * Tether behavior - the object is leashed to a moving target.
 *
 * The leash is `keepDistance` long: the object approaches at up to `speed`
 * when the target pulls beyond it (after an optional `delay`), rests
 * anywhere inside it, and backs off when the target leans in closer than
 * keepDistance × (1 − REST_BAND) — creatures and companions.
 *
 * Deliberately NOT named `Follow`: legacy Unity-era scenes author a
 * `Follow` behavior that is an exact transform constraint, not a leash.
 * That `@type` stays retired so the two can never be confused — see
 * `loaders/LegacyConstraints.ts`.
 *
 * `axes` constrains movement to world axes (the classic AR panel follows on
 * x/z, holding its authored height).
 *
 * Tether moves position only. To face while following, compose with a
 * Billboard behavior — behaviors stack by design. To anchor an object at a
 * gaze-frame offset instead ("summoned UI lands in view"), use the
 * ViewAnchor behavior.
 *
 * RuntimeToggleable: the graph can switch following on/off at play time
 * (trace/setBehaviorEnabled). Disabling stops the object where it is;
 * re-enabling re-runs `delay` (notice, then catch up). The authored
 * `enabled` field is the state at every play-mode start.
 */
export class Tether extends TraceBehavior<TetherData> implements RuntimeToggleable {
    static readonly behaviorType = "Tether" as const;

    private _observer: Observer<Scene> | null = null;
    private _lastFrameTime: number | null = null;
    private _outOfRangeSince: number | null = null;
    private _targetObj: SceneObject | null = null;
    /** Runtime on/off + the Scene-Starts play-start latch (see RuntimeToggleLatch). */
    private readonly _toggle = new RuntimeToggleLatch();

    setRuntimeEnabled(enabled: boolean): void {
        if (this._toggle.set(enabled, this._observer !== null)) {
            // Fresh clock + delay on every toggle: re-enable "notices, then
            // catches up"; a stale dt can't produce a jump either way.
            this._lastFrameTime = null;
            this._outOfRangeSince = null;
        }
    }

    isRuntimeEnabled(): boolean {
        return this._toggle.enabled;
    }

    protected onPlayModeChanged(playing: boolean): void {
        if (playing) {
            this._applyTether();
        } else {
            this._removeTether();
        }
    }

    protected onDetach(): void {
        this._removeTether();
    }

    protected async applyCommit(_patch: Partial<TetherData>): Promise<void> {
        this._removeTether();
        if (this.playMode) {
            this._applyTether();
        }
    }

    /** Resolve the followed object, cached until disposed. Null = camera anchor. */
    private _resolveAnchorObject(): SceneObject | null {
        const path = this._resolved.target ?? "";
        if (!path) return null;
        if (!this._targetObj || this._targetObj.transform.isDisposed()) {
            this._targetObj = this.sceneObject.traceScene.resolveSceneObject(path, this.sceneObject);
        }
        return this._targetObj;
    }

    /** Resolve the followed position into `out`. Returns false when unavailable. */
    private _resolveTargetPosition(out: Vector3): boolean {
        const path = this._resolved.target ?? "";
        if (!path) {
            const camera = this.sceneObject.babylonScene.activeCamera;
            if (!camera) return false;
            out.copyFrom(camera.globalPosition);
            return true;
        }
        const obj = this._resolveAnchorObject();
        // Reject self AND the whole own subtree: following your own
        // descendant is unbounded runaway drift — moving self moves the
        // target, leaving a constant delta to chase every frame.
        if (!obj || obj === this.sceneObject
            || obj.transform.isDescendantOf(this.sceneObject.transform)) return false;
        out.copyFrom(obj.transform.getAbsolutePosition());
        return true;
    }

    private _applyTether(): void {
        const scene = this.sceneObject.babylonScene;
        this._lastFrameTime = null;
        this._outOfRangeSince = null;
        this._targetObj = null;
        // Every play start resets the runtime toggle to the authored state —
        // unless an enable already landed this play-start window (Scene
        // Starts trigger), which the reset consumes rather than stomps
        this._toggle.reset(this._resolved.enabled ?? true);

        this._observer = scene.onBeforeRenderObservable.add(() => {
            if (!this._toggle.enabled) return;

            const r = this._resolved;
            const keepDistance = Math.max(0, r.keepDistance ?? 1);
            const speed = Math.max(0, r.speed ?? 1.5);
            if (speed === 0) return;

            if (!this._resolveTargetPosition(_tmpTargetPos)) return;

            const transform = this.sceneObject.transform;
            transform.computeWorldMatrix(true);
            _tmpSelfPos.copyFrom(transform.getAbsolutePosition());

            // Movement delta constrained to the allowed world axes — the
            // distance band is measured in the same masked space, so a
            // height-locked follower doesn't endlessly chase a vertical gap.
            _tmpDelta.copyFrom(_tmpTargetPos).subtractInPlace(_tmpSelfPos);
            const axes = r.axes;
            if (axes) {
                if (!axes.includes("x")) _tmpDelta.x = 0;
                if (!axes.includes("y")) _tmpDelta.y = 0;
                if (!axes.includes("z")) _tmpDelta.z = 0;
            }
            const dist = _tmpDelta.length();

            // Wall-clock dt (framerate-independent, 100 ms hitch clamp)
            const now = performance.now();
            const dt = this._lastFrameTime === null
                ? 0
                : Math.min((now - this._lastFrameTime) / 1000, 0.1);
            this._lastFrameTime = now;

            const tooFar = dist > keepDistance;
            const tooClose = keepDistance > 0 && dist < keepDistance * (1 - REST_BAND);

            // Approach honors the delay ("notice, then catch up"); backing
            // off from a leaning-in target reacts immediately.
            if (tooFar) {
                if (this._outOfRangeSince === null) this._outOfRangeSince = now;
                const delay = (r.delay ?? 0) * 1000;
                if (now - this._outOfRangeSince < delay) return;
            } else {
                this._outOfRangeSince = null;
                if (!tooClose) return; // inside the comfort band — rest
            }
            if (dt === 0 || dist < 1e-6) return;

            // Desired resting point: keepDistance short of the target along
            // the (masked) line between them
            _tmpDesired.copyFrom(_tmpDelta).scaleInPlace((dist - keepDistance) / dist);

            // Eased approach with a hard speed cap
            const damping = r.damping ?? 0.25;
            _tmpStep.copyFrom(_tmpDesired);
            if (damping > 0) {
                _tmpStep.scaleInPlace(1 - Math.exp(-dt / damping));
            }
            const maxStep = speed * dt;
            const stepLen = _tmpStep.length();
            if (stepLen > maxStep) {
                _tmpStep.scaleInPlace(maxStep / stepLen);
            }

            _tmpSelfPos.addInPlace(_tmpStep);
            transform.setAbsolutePosition(_tmpSelfPos);
        });
    }

    private _removeTether(): void {
        if (this._observer) {
            this.sceneObject.babylonScene.onBeforeRenderObservable.remove(this._observer);
            this._observer = null;
        }
        this._lastFrameTime = null;
        this._outOfRangeSince = null;
        this._targetObj = null;
        this._toggle.clearPending();
    }
}
